import { Form, Radio } from 'antd';
import t from 'utils/translations';
import CompareInput from 'components/companysearch/CompareCompanySearch';
import * as React from 'react';
import ModalBase from 'components/common/ModalBase';
import { ModalProps } from 'antd/es/modal';
import {
    AggregationKeyFigureResponse,
    GroupWithCategoryName,
    Industry,
    IndustryResponse,
    KeyFigureData,
    KeyFiguresResponse,
    PageResponse
} from 'components/context/types';
import { useEffect, useState } from 'react';
import { SecondaryCompanyInfo } from 'components/common/KeyFigureGraph/CompanyKeyFigureGraphs';
import { useAppContextOrThrow } from 'utils';

import GroupTreeSelector from 'components/CommonGroupAndCategoryStuff/GroupTreeSelector';
import { prepareAggregationsToKeyFigureData } from 'utils/keyfigures';
import {
    apiGetGroupKeyFigureAggregations,
    apiGetUsersGroupsWithCategoryNames
} from 'api/groups';
import { categorizeGroups } from 'utils/groupUtils';
import {
    apiGetIndustryCodes,
    apiGetIndustryKeyFigureAggregations
} from 'api/industries';
import IndustryTreeSelector from 'components/common/KeyFigureGraph/IndustryTreeSelector';
import { apiGetCategoryKeyFigureAggregations } from 'api/categories';

interface CompareGraphModalProps extends ModalProps {
    closeModal: () => void;
    setSecondaryDataSet: (secondaryDataSet: KeyFigureData | undefined) => void;
}

function CompareGraphModal({
    closeModal,
    setSecondaryDataSet,
    visible
}: CompareGraphModalProps) {
    const context = useAppContextOrThrow();

    const [selectedCompareTarget, setSelectedCompareTarget] = useState<
        'company-radio' | 'group-radio' | 'industry-radio'
    >('company-radio');

    const [secondaryDataSetCompany, setSecondaryDataSetCompany] = useState<
        SecondaryCompanyInfo | undefined
    >(undefined);

    const [groupsWithCategoryNames, setGroupsWithCategoryNames] = useState<
        GroupWithCategoryName[]
    >([]);

    const [industryTree, setIndustryTree] = useState<Industry[]>([]);

    const [
        secondaryDataSetGroupOrCategoryId,
        setSecondaryDataSetGroupOrCategoryId
    ] = useState<string | undefined>(undefined);

    const [secondaryDataSetIndustry, setSecondaryDataSetIndustry] = useState<
        string | undefined
    >(undefined);

    useEffect(() => {
        if (visible) {
            apiGetUsersGroupsWithCategoryNames()
                .then(res => res.json())
                .then((data: PageResponse<GroupWithCategoryName>) => {
                    setGroupsWithCategoryNames(data.content);
                });
        }
    }, [visible]);

    useEffect(() => {
        apiGetIndustryCodes()
            .then(res => res.json())
            .then((data: IndustryResponse) => {
                setIndustryTree(data.content);
            });
    }, []);

    function onGroupSelect(value: string) {
        setSecondaryDataSetGroupOrCategoryId(value);
    }

    function onIndustrySelect(value: string) {
        setSecondaryDataSetIndustry(value);
    }

    function onOk() {
        switch (selectedCompareTarget) {
            case 'company-radio':
                setCompanyAsSecondaryDataSet();
                break;
            case 'group-radio':
                setGroupAsSecondaryDataSet();
                break;
            case 'industry-radio':
                setIndustryAsSecondaryDataSet();
                break;
        }
    }

    function renderSelectedFormItem(
        compareTargetType: 'company-radio' | 'group-radio' | 'industry-radio'
    ) {
        switch (compareTargetType) {
            case 'company-radio':
                return (
                    <Form.Item colon={false} label={t('CHOOSE_COMPANY')}>
                        <CompareInput
                            setSearchResult={setSecondaryDataSetCompany}
                        />
                    </Form.Item>
                );
            case 'group-radio':
                return (
                    <Form.Item colon={false} label={t('CHOOSE_GROUP')}>
                        <GroupTreeSelector
                            allowCategorySelection={true}
                            groupsByCategories={categorizeGroups(
                                groupsWithCategoryNames
                            )}
                            onSelect={onGroupSelect}
                            value={secondaryDataSetGroupOrCategoryId}
                        />
                    </Form.Item>
                );
            case 'industry-radio':
                return (
                    <Form.Item colon={false} label={t('CHOOSE_INDUSTRY')}>
                        <IndustryTreeSelector
                            industryTree={industryTree}
                            onSelect={onIndustrySelect}
                            value={secondaryDataSetIndustry}
                        />
                    </Form.Item>
                );
            default:
                return <></>;
        }
    }

    function setCompanyAsSecondaryDataSet() {
        if (secondaryDataSetCompany) {
            context.actions
                .fetchKeyFigures(secondaryDataSetCompany.businessId, 10)
                .then(res => res.json())
                .then((data: KeyFiguresResponse) => {
                    setSecondaryDataSet({
                        seriesName: secondaryDataSetCompany.name,
                        keyFigures:
                            data.concernKeyFigures.length > 0
                                ? data.concernKeyFigures
                                : data.keyFigures
                    });
                    closeModal();
                });
        }
    }

    function setGroupAsSecondaryDataSet() {
        if (secondaryDataSetGroupOrCategoryId) {
            const groupInfo = groupsWithCategoryNames.find(
                group => group.groupId === secondaryDataSetGroupOrCategoryId
            );

            if (groupInfo) {
                apiGetGroupKeyFigureAggregations(
                    secondaryDataSetGroupOrCategoryId
                )
                    .then(res => res.json())
                    .then((data: AggregationKeyFigureResponse) => {
                        setSecondaryDataSet(
                            prepareAggregationsToKeyFigureData(
                                data.content,
                                groupInfo ? groupInfo.groupName : ''
                            )
                        );
                    });
            } else {
                const categoryInfo = groupsWithCategoryNames.find(
                    group =>
                        group.categoryId === secondaryDataSetGroupOrCategoryId
                );
                apiGetCategoryKeyFigureAggregations(
                    secondaryDataSetGroupOrCategoryId
                )
                    .then(res => res.json())
                    .then((data: AggregationKeyFigureResponse) => {
                        setSecondaryDataSet(
                            prepareAggregationsToKeyFigureData(
                                data.content,
                                categoryInfo ? categoryInfo.categoryName : ''
                            )
                        );
                    });
            }

            closeModal();
        }
    }

    function setIndustryAsSecondaryDataSet() {
        if (secondaryDataSetIndustry) {
            const [
                industryCode,
                industryLabel
            ] = secondaryDataSetIndustry.split('|');
            apiGetIndustryKeyFigureAggregations(industryCode)
                .then(res => res.json())
                .then((data: AggregationKeyFigureResponse) => {
                    setSecondaryDataSet(
                        prepareAggregationsToKeyFigureData(
                            data.content,
                            industryLabel
                        )
                    );
                });
            closeModal();
        }
    }

    return (
        <ModalBase
            okText={t('COMPARE')}
            onCancel={() => closeModal()}
            onOk={() => onOk()}
            title={t('COMPARE_GRAPH_MODAL_TITLE')}
            visible={visible}
        >
            <Form>
                <Form.Item colon={false} label={t('CHOOSE_COMPARE_TARGET')}>
                    <Radio.Group
                        onChange={event =>
                            setSelectedCompareTarget(event.target.value)
                        }
                        value={selectedCompareTarget}
                    >
                        <Radio value={'company-radio'}>{t('COMPANY')}</Radio>
                        <Radio value={'group-radio'}>{t('GROUP')}</Radio>
                        <Radio value={'industry-radio'}>{t('INDUSTRY')}</Radio>
                    </Radio.Group>
                </Form.Item>
                {renderSelectedFormItem(selectedCompareTarget)}
            </Form>
        </ModalBase>
    );
}
export default CompareGraphModal;
